import { ticketOntologyFieldsOf } from 'features/libra/ontology/useOntologyFields'
import { useTicketsSignoffEntries } from 'features/libra/signoff/signoffsApi'
import type { NiiZoneImage } from 'features/libra/stepComponents/niiReport/content/niiReportContentTypes'
import { getNiiReportConfig } from 'features/libra/stepComponents/niiReport/niiReportApi'
import {
  drawZonesOnCanvas,
  zoneBoxesToCanvasMarks,
} from 'features/libra/stepComponents/niiReport/zoneUtils'
import {
  parseZonesFromFieldValues,
  type ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import { useRolodexEntries, useRolodexSignedURLs } from 'features/rolodex'
import uniq from 'lodash/uniq'
import { useCallback, useMemo, useState } from 'react'
import { RolodexEntryType, type RolodexFilePath } from 'types'
import { downloadBlob } from 'utils/download'
import { fetchImageAsBase64 } from 'utils/imageUtils'

import {
  buildNiiReportDownloadFilename,
  buildNiiReportPdfBlob,
} from './buildNiiReportPdfBlob'

export interface BulkNiiPdfExportProgress {
  done: number
  total: number
}

export interface BulkNiiPdfExportSummary {
  /**
   * Tickets whose PDF was generated AND handed to the browser to save. Note
   * that a browser can still refuse the save itself (e.g. declining the
   * "download multiple files" prompt), which is not observable from script.
   */
  succeeded: ReadonlyArray<LibraTicket>
  failed: ReadonlyArray<{ ticket: LibraTicket; error: unknown }>
}

/** Spacing between sequential `downloadBlob` calls — browsers throttle or
 * prompt on programmatic downloads fired back-to-back. */
const DOWNLOAD_STAGGER_MS = 300

/**
 * PDF generation fetches Rolodex images and renders with `@react-pdf/renderer`
 * per ticket — running every ticket at once for a large selection can spike
 * memory and network use. Capping concurrency keeps a big batch responsive
 * without materially slowing small ones.
 */
const MAX_CONCURRENT_PDF_GENERATIONS = 4

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Runs `task` over `items` with at most `limit` in flight at once, each
 * worker picking up the next item as soon as it finishes one (rather than
 * fixed batches) so uneven per-item durations don't leave workers idle.
 * `task` receives the item's original index, since completion order and
 * input order differ once more than one runs at a time.
 */
async function runWithConcurrencyLimit<T>(
  items: ReadonlyArray<T>,
  limit: number,
  task: (item: T, index: number) => Promise<void>
): Promise<void> {
  let nextIndex = 0
  async function worker(): Promise<void> {
    while (nextIndex < items.length) {
      const index = nextIndex
      nextIndex += 1
      await task(items[index], index)
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker)
  )
}

interface GeneratedPdf {
  ticket: LibraTicket
  filename: string
  blob: Blob
}

/**
 * Generates and downloads one NII PDF report per given ticket, without
 * mounting any ticket-scoped provider tree. Every context-bound hook the
 * single-ticket export (`useNiiPdfExport`) relies on has a pure/explicit
 * equivalent here, fed by data the caller already holds (the tickets and a
 * `workflowMap` covering their `workflowId`s) plus a few batched Rolodex
 * fetches for asset/site names and zone reference images.
 *
 * Caller-agnostic by design: it takes plain tickets rather than reading any
 * screen's state, so the Explorer NII PDF panel can drive it from a
 * drilldown scope with no Libra dashboard in the tree.
 */
export function useBulkNiiPdfExport(
  tickets: ReadonlyArray<LibraTicket>,
  workflowMap: ReadonlyMap<string, LibraWorkflow>,
  organizationId: string
): {
  generateAndDownload: () => Promise<BulkNiiPdfExportSummary>
  isGenerating: boolean
  progress: BulkNiiPdfExportProgress | undefined
  /** True until sign-offs load; exporting earlier would print them unsigned. */
  isLoading: boolean
} {
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState<BulkNiiPdfExportProgress>()

  const unitIds = useMemo(
    () =>
      uniq(
        tickets
          .map((ticket) => ticket.tags.unit)
          .filter((id): id is string => !!id)
      ),
    [tickets]
  )
  const { units } = useRolodexEntries(
    useMemo(
      () =>
        unitIds.length > 0
          ? {
              type: RolodexEntryType.UNIT,
              ids: unitIds,
              tags: { organization: organizationId },
            }
          : undefined,
      [unitIds, organizationId]
    )
  )
  const unitsById = useMemo(
    () => new Map(units.map((unit) => [unit.id, unit])),
    [units]
  )

  const siteIds = useMemo(
    () =>
      uniq(
        units.map((unit) => unit.tags.site).filter((id): id is string => !!id)
      ),
    [units]
  )
  const { sites } = useRolodexEntries(
    useMemo(
      () =>
        siteIds.length > 0
          ? { type: RolodexEntryType.SITE, ids: siteIds }
          : undefined,
      [siteIds]
    )
  )
  const sitesById = useMemo(
    () => new Map(sites.map((site) => [site.id, site])),
    [sites]
  )

  const ticketIds = useMemo(() => tickets.map(({ id }) => id), [tickets])
  const { signoffsByTicketId, isLoading: signoffsLoading } =
    useTicketsSignoffEntries(ticketIds)

  // ── Zones per ticket, using each ticket's own workflow-configured field ──
  const zonesByTicketId = useMemo(() => {
    const map = new Map<string, ReadonlyArray<ZoneParams>>()
    tickets.forEach((ticket) => {
      const workflow = workflowMap.get(ticket.workflowId)
      const zoneFieldName =
        getNiiReportConfig(workflow?.steps)?.zoneFieldName ?? 'zones'
      const { zones } = parseZonesFromFieldValues(
        ticket.fieldValues,
        zoneFieldName
      )
      map.set(ticket.id, zones)
    })
    return map
  }, [tickets, workflowMap])

  const zoneImageIds = useMemo(() => {
    const ids = new Set<string>()
    zonesByTicketId.forEach((zones) => {
      zones.forEach((zone) => {
        if (zone.zone_reference_image_id) {
          ids.add(zone.zone_reference_image_id)
        }
      })
    })
    return [...ids]
  }, [zonesByTicketId])

  const { reports: imageEntries } = useRolodexEntries(
    useMemo(
      () =>
        zoneImageIds.length > 0
          ? { ids: zoneImageIds, type: [RolodexEntryType.REPORT] }
          : undefined,
      [zoneImageIds]
    )
  )

  const imageFileQueries = useMemo<
    ReadonlyArray<RolodexFilePath> | undefined
  >(() => {
    const entries = imageEntries
      .map((doc) => {
        const filename = doc.raw.data.filename as string | undefined
        return filename ? { entry_id: doc.id, path: filename } : null
      })
      .filter((entry): entry is RolodexFilePath => entry !== null)
    return entries.length > 0 ? entries : undefined
  }, [imageEntries])

  const { results: imageSignedResults } = useRolodexSignedURLs(imageFileQueries)

  const imagesById = useMemo(() => {
    const urlMap = new Map(
      imageSignedResults.map((r) => [r.entry_id, r.signed_url])
    )
    return new Map(
      imageEntries.map((doc) => {
        const filename = doc.raw.data.filename as string | undefined
        return [
          doc.id,
          {
            url: urlMap.get(doc.id),
            name: (doc.data.visibleName ||
              filename ||
              doc.id.substring(0, 8)) as string,
          },
        ] as const
      })
    )
  }, [imageEntries, imageSignedResults])

  const generateOne = useCallback(
    async (ticket: LibraTicket): Promise<GeneratedPdf> => {
      const workflow = workflowMap.get(ticket.workflowId)
      const reportConfig = getNiiReportConfig(workflow?.steps)
      if (!workflow || !reportConfig) {
        throw new Error(
          `Ticket "${ticket.title}" has no NII report configuration`
        )
      }
      const { fields } = reportConfig
      const zones = zonesByTicketId.get(ticket.id) ?? []

      const unit = ticket.tags.unit
        ? unitsById.get(ticket.tags.unit)
        : undefined
      const site = unit?.tags.site ? sitesById.get(unit.tags.site) : undefined
      const assetName = unit?.data.name as string | undefined
      const siteName = site?.data.name as string | undefined

      const resolvedZoneImages = (
        await Promise.all(
          zones
            .filter(
              (zone) =>
                zone.zone_reference_image_id &&
                Boolean(zone.drawing_bounds?.length)
            )
            .map(async (zone) => {
              const image = imagesById.get(
                zone.zone_reference_image_id as string
              )
              if (!image?.url) return null
              const imageZones = zones.filter(
                (z) =>
                  z.zone_reference_image_id === zone.zone_reference_image_id
              )
              try {
                const dataUrl = await fetchImageAsBase64(
                  image.url,
                  undefined,
                  (ctx, imgEl) =>
                    drawZonesOnCanvas(
                      ctx,
                      imgEl.naturalHeight,
                      zoneBoxesToCanvasMarks(imageZones)
                    )
                )
                return { name: image.name, dataUrl }
              } catch {
                return null
              }
            })
        )
      ).filter((img): img is NiiZoneImage => img !== null)

      // Multiple zones can share one reference image — render it once.
      const zoneImages = [
        ...new Map(resolvedZoneImages.map((img) => [img.name, img])).values(),
      ]

      const blob = await buildNiiReportPdfBlob({
        workflowSteps: workflow.steps,
        fieldValues: ticket.fieldValues,
        ontologyFields: ticketOntologyFieldsOf(
          workflow.globalContext.ontologyFields
        ),
        zones,
        zoneImages,
        reportFields: fields,
        signoffConfig: workflow.signoffConfig,
        signoffs: signoffsByTicketId.get(ticket.id) ?? {},
        lifecycle: {
          config: workflow.lifecycleConfig,
          ticketStatus: ticket.ticketStatus,
          terminalTransitionBy: ticket.terminalTransitionBy,
          terminalTransitionAt: ticket.terminalTransitionAt,
          submittedForReviewBy: ticket.submittedForReviewBy,
          submittedForReviewAt: ticket.submittedForReviewAt,
        },
        siteName,
        assetName,
        analysisVersion: ticket.version,
      })

      return {
        ticket,
        blob,
        filename: buildNiiReportDownloadFilename(
          siteName,
          assetName,
          ticket.version
        ),
      }
    },
    [
      workflowMap,
      zonesByTicketId,
      unitsById,
      sitesById,
      imagesById,
      signoffsByTicketId,
    ]
  )

  const generateAndDownload =
    useCallback(async (): Promise<BulkNiiPdfExportSummary> => {
      setIsGenerating(true)
      setProgress({ done: 0, total: tickets.length })

      const succeeded: LibraTicket[] = []
      const failed: { ticket: LibraTicket; error: unknown }[] = []

      // One slot per ticket, filled in completion order but saved in
      // selection order. `undefined` = still generating; `null` = nothing
      // left to save (generation failed, or the blob has been handed to the
      // browser and released).
      const slots = new Array<GeneratedPdf | null | undefined>(tickets.length)
      let nextToSave = 0
      let savedCount = 0

      /**
       * Saves every slot that is ready, in order, stopping at the first gap.
       * Files therefore reach disk in the order the user selected them, and
       * each blob is released as soon as it's saved — peak memory is the
       * handful in flight rather than every PDF in the export.
       */
      const saveReadySlots = async (): Promise<void> => {
        while (nextToSave < tickets.length && slots[nextToSave] !== undefined) {
          const pdf = slots[nextToSave]
          slots[nextToSave] = null
          nextToSave += 1
          if (!pdf) continue
          if (savedCount > 0) await sleep(DOWNLOAD_STAGGER_MS)
          try {
            downloadBlob(pdf.blob, pdf.filename)
            savedCount += 1
            succeeded.push(pdf.ticket)
          } catch (error) {
            failed.push({ ticket: pdf.ticket, error })
          }
        }
      }

      // Saves run one at a time and in the order queued, so awaiting the
      // last link waits for every queued save to finish.
      let saveChain: Promise<void> = Promise.resolve()
      const queueSave = (): Promise<void> => {
        saveChain = saveChain.then(saveReadySlots)
        return saveChain
      }

      try {
        await runWithConcurrencyLimit(
          tickets,
          MAX_CONCURRENT_PDF_GENERATIONS,
          async (ticket, index) => {
            try {
              slots[index] = await generateOne(ticket)
            } catch (error) {
              slots[index] = null
              failed.push({ ticket, error })
            } finally {
              setProgress((prev) =>
                prev ? { done: prev.done + 1, total: prev.total } : prev
              )
            }
            void queueSave()
          }
        )
        await queueSave()
      } finally {
        setIsGenerating(false)
        setProgress(undefined)
      }

      return { succeeded, failed }
    }, [tickets, generateOne])

  return {
    generateAndDownload,
    isGenerating,
    progress,
    isLoading: signoffsLoading,
  }
}
